import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames/bind'
import { QRCode, Text, Drawer, Carousel, Icon } from '@adc/ui-adc'
import config from '@adc/configment'
import Image1 from './remote-bid-1.png'
import Image2 from './remote-bid-2.png'
import AppStoreImg from './appstore.png'
import GooglePlayImg from './googleplay.png'
import cls from './styles.scss'

const cx = classNames.bind(cls)

const STATIC_SLIDE_CONTENTS = [
  {
    title: 'Remote Bid in a nutshell.',
    content:
      'Foreclosure auctions typically take place in person. With Remote Bid, you’ve got a front-row seat to select auctions, all from the convenience of your mobile device.',
    img: Image1,
  },
  {
    title: 'Easy Convenience.',
    content:
      'Track bids in real time, follow the auction action, and adjust your bid with the click of a button. *',
    img: Image2,
    footer:
      '* For properties in Cook County, IL, you must submit your bid by the end of the day, one day prior to auction.',
  },
]

const baseElmId = 'remote_bid_drawer'

const NextArrow = ({ onClick, className }) => {
  const isDisabled = className.includes('slick-disabled')
  return (
    !isDisabled && (
      <div id='next' onClick={onClick} className={cx('arrow', 'next')}>
        <Icon
          name='angle-right'
          color='gray'
          iconStyle='regular'
          size='big'
          data-elm-id={`${baseElmId}_next_arrow`}
        />
      </div>
    )
  )
}

const BackArrow = ({ onClick, className }) => {
  const isDisabled = className.includes('slick-disabled')
  return (
    !isDisabled && (
      <div id='back' onClick={onClick} className={cls.arrow}>
        <Icon
          name='angle-left'
          color='gray'
          iconStyle='regular'
          size='big'
          data-elm-id={`${baseElmId}_back_arrow`}
        />
      </div>
    )
  )
}

NextArrow.propTypes = BackArrow.propTypes = {
  onClick: PropTypes.func,
  className: PropTypes.string,
}

export const getAppStoreContents = (userAgent, config) => {
  const appContents = [
    <a
      className={cx('app-store-img')}
      key={`${baseElmId}_apple`}
      data-elm-id={`${baseElmId}_app_store_link`}
      href={config.get('APP_STORE_LINK_IOS')}
      target='_blank'
      rel='noreferrer'
    >
      <img src={AppStoreImg} />
    </a>,
    <a
      className={cx('app-store-img')}
      key={`${baseElmId}_android`}
      data-elm-id={`${baseElmId}_google_play_link`}
      href={config.get('APP_STORE_LINK_ANDROID')}
      target='_blank'
      rel='noreferrer'
    >
      <img src={GooglePlayImg} />
    </a>,
  ]

  const isMobile = /Android|webOS|iPhone|iPad|iPod/i.test(userAgent)
  if (!isMobile) {
    return appContents
  }

  const isAndroid = /Android/i.test(userAgent)
  return (isAndroid && [appContents[1]]) || [appContents[0]]
}

const RemoteBidDrawer = ({ onClose, className = '' }) => {
  const [userAgent, setUserAgent] = useState('desktop')
  const appContents = getAppStoreContents(userAgent, config)

  useEffect(() => {
    navigator && setUserAgent(navigator.userAgent)
  }, [userAgent])

  return (
    <Drawer
      open
      onClose={onClose}
      elmId={baseElmId}
      closeOnDimmerClick
      headerStyle='clear'
    >
      <div className={cx('drawer-content', className)}>
        <Carousel
          elmId={`${baseElmId}_drawer`}
          settings={{
            dots: true,
            infinite: false,
            slidesToShow: 1,
            nextArrow: <NextArrow />,
            prevArrow: <BackArrow />,
          }}
        >
          {STATIC_SLIDE_CONTENTS.map(slide => {
            const { title, content, img, footer = null } = slide

            return (
              <div className={cls.slide} key={title}>
                <Text.Header color='gray' weight='400' level='2'>
                  {title}
                </Text.Header>
                <div className={cls.content}>
                  <Text.Body color='gray'>{content}</Text.Body>
                </div>
                <div>
                  <img src={img} className={cls.img} />
                </div>
                <Text.Body color='gray' weight='400'>
                  {footer}
                </Text.Body>
              </div>
            )
          })}
          <div className={cls.slide}>
            <Text.Header color='gray' weight='400' level='2'>
              Ready to Join the Action?
            </Text.Header>
            <div className={cls.content}>
              <Text.Body color='gray'>
                Scan the QR code with your mobile device or follow the links
                below to get started with the Auction.com mobile app.
              </Text.Body>
              <div className={cls.qrcode} data-elm-id={`${baseElmId}_qrcode`}>
                <QRCode value={config.get('APP_STORE_LINK_QRCODE')} />
              </div>
              {appContents}
            </div>
          </div>
        </Carousel>
      </div>
    </Drawer>
  )
}

RemoteBidDrawer.propTypes = {
  onClose: PropTypes.func,
  className: PropTypes.string,
}

export default RemoteBidDrawer
